import React, { useState, useCallback, useMemo } from "react";
import { useRouter } from "next/router";
import Head from "next/head";
import { useTranslations } from "next-intl";
import { WEBPAGE_TITLE } from "@utils/Constants";
import Page from "@components/Layout/page";
import { ContentContainer } from "@components/ContentContainer/ContentContainer";
import {
    CATEGORIES,
    getQuestionsForCategory,
} from "../../../data/diagnosticQuestions";
import { buildFullResult } from "../../../utils/scoringEngine";
import DiagnosticRing from "../../DiagnosticQuiz/DiagnosticRing";
import QuizStep from "../../DiagnosticQuiz/QuizStep";
import QuizNavigation from "../../DiagnosticQuiz/QuizNavigation";
import CompositeScores from "../../DiagnosticResults/CompositeScores";
import ResultsRadarChart from "../../DiagnosticResults/ResultsRadarChart";
import PackageRecommendation from "../../DiagnosticResults/PackageRecommendation";
import InsightCards from "../../DiagnosticResults/InsightCards";
import ResultsCTA from "../../DiagnosticResults/ResultsCTA";
import { submitToCRM } from "../../../utils/crmSubmit";
import {
    trackDiagnosticStarted,
    trackStepViewed,
    trackStepCompleted,
    trackBackClicked,
    trackResultViewed,
    trackPackageRecommended,
    trackCRMSubmitSuccess,
    trackCRMSubmitFailed,
} from "../../../utils/diagnosticAnalytics";
import styles from "./DiagnosticPage.module.scss";

const PHASE_LANDING = "landing";
const PHASE_QUIZ = "quiz";
const PHASE_RESULTS = "results";

export default function DiagnosticPage() {
    const { locale } = useRouter();
    const t = useTranslations("DiagnosticPage");
    const [phase, setPhase] = useState(PHASE_LANDING);
    const [contact, setContact] = useState({
        name: "",
        email: "",
        company: "",
    });
    const [answers, setAnswers] = useState({});
    const [currentStep, setCurrentStep] = useState(0);

    const handleContactChange = useCallback((field, value) => {
        setContact((prev) => ({ ...prev, [field]: value }));
    }, []);

    const handleStartQuiz = useCallback(
        (e) => {
            e.preventDefault();
            if (!contact.name || !contact.email) return;
            trackDiagnosticStarted();
            setPhase(PHASE_QUIZ);
        },
        [contact],
    );

    const handleAnswer = useCallback((questionId, value) => {
        setAnswers((prev) => ({ ...prev, [questionId]: value }));
    }, []);

    const currentCategory = CATEGORIES[currentStep];
    const currentQuestions = currentCategory
        ? getQuestionsForCategory(currentCategory.id)
        : [];
    const allCurrentAnswered = currentQuestions.every(
        (q) => answers[q.id] != null,
    );

    const completedSteps = useMemo(() => {
        const completed = [];
        CATEGORIES.forEach((cat, i) => {
            const qs = getQuestionsForCategory(cat.id);
            if (qs.every((q) => answers[q.id] != null)) {
                completed.push(i);
            }
        });
        return completed;
    }, [answers]);

    const handleContinue = useCallback(() => {
        if (!allCurrentAnswered) return;
        const catName = CATEGORIES[currentStep]?.name_is || "";
        trackStepCompleted(currentStep + 1, catName, 0);
        if (currentStep < CATEGORIES.length - 1) {
            setCurrentStep((prev) => prev + 1);
        } else {
            setPhase(PHASE_RESULTS);
        }
    }, [allCurrentAnswered, currentStep]);

    const handleBack = useCallback(() => {
        if (currentStep > 0) {
            trackBackClicked(currentStep + 1, currentStep);
            setCurrentStep((prev) => prev - 1);
        }
    }, [currentStep]);

    const result = useMemo(() => {
        if (phase !== PHASE_RESULTS) return null;
        return buildFullResult(answers);
    }, [phase, answers]);

    React.useEffect(() => {
        if (phase === PHASE_RESULTS && result) {
            trackResultViewed(result.recommendation, result.composite);
            trackPackageRecommended(result.recommendation, result.composite);
            submitToCRM({ contact, result })
                .then((res) => {
                    if (res.success)
                        trackCRMSubmitSuccess(result.recommendation);
                    else trackCRMSubmitFailed("unknown", result.recommendation);
                })
                .catch(() =>
                    trackCRMSubmitFailed("network", result.recommendation),
                );
        }
    }, [phase]);

    const isLanding = phase === PHASE_LANDING;
    const isQuiz = phase === PHASE_QUIZ;
    const isResults = phase === PHASE_RESULTS;

    return (
        <Page fluid>
            <Head>
                <title key="site_title">
                    {t("page_title")} - {WEBPAGE_TITLE}
                </title>
                <meta name="description" content={t("hero_desc")} />
            </Head>
            <ContentContainer>
                <div className={styles.diagnosticPage}>
                    {isLanding && (
                        <div className={styles.landingContainer}>
                            <div className={styles.landingContent}>
                                <h1 className={styles.heroTitle}>
                                    {t("hero_title")}
                                </h1>
                                <p className={styles.heroSubtitle}>
                                    {t("hero_subtitle")}
                                </p>
                                <p className={styles.heroDesc}>
                                    {t("hero_desc")}
                                </p>

                                <div className={styles.scopeNote}>
                                    <strong>{t("scope_title")}</strong>
                                    <span>{t("scope_body")}</span>
                                </div>

                                <form
                                    className={styles.leadForm}
                                    onSubmit={handleStartQuiz}
                                >
                                    <input
                                        type="text"
                                        placeholder={t("name")}
                                        value={contact.name}
                                        onChange={(e) =>
                                            handleContactChange(
                                                "name",
                                                e.target.value,
                                            )
                                        }
                                        required
                                        className={styles.input}
                                    />
                                    <input
                                        type="email"
                                        placeholder={t("email")}
                                        value={contact.email}
                                        onChange={(e) =>
                                            handleContactChange(
                                                "email",
                                                e.target.value,
                                            )
                                        }
                                        required
                                        className={styles.input}
                                    />
                                    <input
                                        type="text"
                                        placeholder={t("company")}
                                        value={contact.company}
                                        onChange={(e) =>
                                            handleContactChange(
                                                "company",
                                                e.target.value,
                                            )
                                        }
                                        className={styles.input}
                                    />
                                    <button
                                        type="submit"
                                        className={styles.startBtn}
                                    >
                                        {t("start")} →
                                    </button>
                                </form>
                            </div>
                        </div>
                    )}

                    {isQuiz && currentCategory && (
                        <div className={styles.quizContainer}>
                            <div className={styles.quizLayout}>
                                <aside className={styles.ringAside}>
                                    <DiagnosticRing
                                        activeStep={currentStep}
                                        completedSteps={completedSteps}
                                    />
                                    <p className={styles.stepLabel}>
                                        {t("step_prefix")} {currentStep + 1}{" "}
                                        {t("of")} 8 —{" "}
                                        {locale === "en"
                                            ? currentCategory.name_en
                                            : currentCategory.name_is}
                                    </p>
                                </aside>

                                <main className={styles.quizMain}>
                                    <QuizStep
                                        category={currentCategory}
                                        answers={answers}
                                        onAnswer={handleAnswer}
                                    />
                                    <QuizNavigation
                                        currentStep={currentStep}
                                        totalSteps={CATEGORIES.length}
                                        canContinue={allCurrentAnswered}
                                        onBack={handleBack}
                                        onContinue={handleContinue}
                                    />
                                </main>
                            </div>
                        </div>
                    )}

                    {isResults && result && (
                        <div className={styles.resultsContainer}>
                            <h1 className={styles.resultsTitle}>
                                {t("results_title")}
                            </h1>
                            {contact.company && (
                                <p className={styles.resultsCompany}>
                                    {contact.company}
                                </p>
                            )}

                            <CompositeScores composite={result.composite} />

                            <div className={styles.resultsGrid}>
                                <div className={styles.resultsPanel}>
                                    <ResultsRadarChart
                                        categoryScores={result.categoryScores}
                                        recommendation={result.recommendation}
                                    />
                                </div>
                                <div className={styles.resultsPanel}>
                                    <PackageRecommendation
                                        recommendation={result.recommendation}
                                        packageText={result.packageText}
                                    />
                                </div>
                            </div>

                            <InsightCards insights={result.insights} />

                            <ResultsCTA
                                recommendation={result.recommendation}
                                contact={contact}
                                result={result}
                            />
                        </div>
                    )}
                </div>
            </ContentContainer>
        </Page>
    );
}
